<template>
  <div class="tree-transfer">
    <!-- 左侧：分区树 + 设备列表 -->
    <div class="transfer-panel">
      <div class="panel-header">分区列表</div>
      <el-tree
        ref="partitionTreeRef"
        :data="fenquArr"
        node-key="id"
        :props="{ label: 'regionName', children: 'children' }"
        :expand-on-click-node="false"
        @node-click="handlePartitionClick"
        highlight-current
        class="partition-tree"
      />

      <div v-if="currentPartition" class="device-section">
        <div class="section-title">
          {{ currentPartition.regionName }} - 设备列表
        </div>
        <el-checkbox-group
          v-model="selectedDeviceIds"
          @change="handleDeviceSelectionChange"
        >
          <el-scrollbar wrap-class="device-scrollbar" max-height="300px">
            <el-checkbox
              v-for="device in currentPartitionDevices"
              :key="device.id"
              :label="device.id"
              :disabled="rightSelectedIds.includes(device.id)"
              class="device-item"
            >
              {{ device.name }}
            </el-checkbox>
          </el-scrollbar>
        </el-checkbox-group>
        <div class="device-actions">
          <el-button
            size="small"
            type="primary"
            @click="selectAllDevices"
            :disabled="currentPartitionDevices.length === 0"
          >
            全选本分区
          </el-button>
          <el-button size="small" @click="clearSelection">
            清空选择
          </el-button>
        </div>
      </div>
    </div>

    <!-- 中间操作按钮 -->
    <div class="transfer-buttons">
      <el-button
        type="primary"
        icon="el-icon-arrow-right"
        @click="moveToRight"
        :disabled="selectedDeviceIds.length === 0"
      />
      <el-button
        icon="el-icon-arrow-left"
        @click="moveToLeft"
        :disabled="rightSelectedIds.length === 0"
      />
    </div>

    <!-- 右侧：已选设备 -->
    <div class="transfer-panel">
      <div class="panel-header">已选设备 ({{ rightSelectedIds.length }})</div>
      <el-scrollbar wrap-class="selected-scrollbar" max-height="400px">
        <div class="selected-list">
          <el-tag
            v-for="item in selectedItems"
            :key="item.deviceId"
            closable
            @close="removeFromRight(item.deviceId)"
            class="selected-item"
          >
            {{ item.partitionName }} - {{ item.deviceName }}
          </el-tag>
        </div>
      </el-scrollbar>
    </div>
  </div>
</template>

<script>
import store from "@/store";

export default {
  name: "FormModal",
  inject: ["form"],
  emits: ["success", "cancel"],
  data() {
    return {
      companyId: null,
      fenquArr: [],
      currentPartition: null,
      currentPartitionDevices: [],
      selectedDeviceIds: [],
      rightSelectedIds: [],
      partitionDeviceMap: new Map(),
      selectedItems: [], // { deviceId, partitionId, partitionName, deviceName }
      isLoadingEcho: false // 防止回显时触发额外的同步
    };
  },
  methods: {
    getROLE() {
      const token = store.getters.get_token;
      if (token) {
        this.companyId = token.companyId;
      }
    },

    async getFenqu() {
      try {
        const res = await this.$api.marketing.allZonePull();
        if (res.code === 200) {
          this.fenquArr = res.data.map(partition => ({
            ...partition,
            children: partition.children || []
          }));
          // 分区加载完成后进行回显
          await this.echoFormData();
        }
      } catch (error) {
        console.error("获取分区失败:", error);
      }
    },

    // 根据 form 中的 equipmentIds 和 regionIds 回显已选设备
    async echoFormData() {
      if (!this.form) return;

      const { equipmentIds, regionIds } = this.form;
      if (!equipmentIds || !regionIds) return;

      this.isLoadingEcho = true;

      try {
        const deviceIdList = equipmentIds
          .split(",")
          .map(id => Number(id))
          .filter(id => id);
        const regionIdList = [
          ...new Set(
            regionIds
              .split(",")
              .map(id => Number(id))
              .filter(id => id)
          )
        ];

        if (deviceIdList.length === 0 || regionIdList.length === 0) return;

        // 并行加载所有涉及分区的设备列表（利用缓存）
        const loadPromises = regionIdList.map(regionId => {
          if (this.partitionDeviceMap.has(regionId)) {
            return Promise.resolve(this.partitionDeviceMap.get(regionId));
          } else {
            return this.$api.dataAnalysis
              .equipmentList({
                companyId: this.companyId,
                regionId
              })
              .then(res => {
                if (res.code === 200) {
                  const devices = res.data || [];
                  this.partitionDeviceMap.set(regionId, devices);
                  return devices;
                }
                return [];
              })
              .catch(() => []);
          }
        });

        await Promise.all(loadPromises);

        // 从缓存中查找设备信息
        const tempSelectedItems = [];
        const tempRightSelectedIds = [];

        for (const deviceId of deviceIdList) {
          let found = false;
          for (const [regionId, devices] of this.partitionDeviceMap.entries()) {
            const device = devices.find(d => d.id === deviceId);
            if (device) {
              // 获取分区名称
              const partition = this.fenquArr.find(p => p.id === regionId);
              const partitionName = partition
                ? partition.regionName
                : `分区${regionId}`;

              tempSelectedItems.push({
                deviceId,
                partitionId: regionId,
                partitionName,
                deviceName: device.name
              });
              tempRightSelectedIds.push(deviceId);
              found = true;
              break;
            }
          }
          if (!found) {
            console.warn(`回显失败：未找到设备 ID ${deviceId}`);
          }
        }

        this.selectedItems = tempSelectedItems;
        this.rightSelectedIds = tempRightSelectedIds;

        // 同步到 form（确保格式一致）
        this.syncFormData();
      } catch (error) {
        console.error("回显数据失败:", error);
      } finally {
        this.isLoadingEcho = false;
      }
    },

    async handlePartitionClick(partitionData) {
      this.selectedDeviceIds = [];
      this.currentPartition = {
        ...partitionData,
        name: partitionData.regionName
      };

      if (this.partitionDeviceMap.has(partitionData.id)) {
        this.currentPartitionDevices = this.partitionDeviceMap.get(
          partitionData.id
        );
        this.syncLeftSelection();
        return;
      }

      try {
        const res = await this.$api.dataAnalysis.equipmentList({
          companyId: this.companyId,
          regionId: partitionData.id
        });
        if (res.code === 200) {
          this.currentPartitionDevices = res.data || [];
          this.partitionDeviceMap.set(
            partitionData.id,
            this.currentPartitionDevices
          );
          this.syncLeftSelection();
        }
      } catch (error) {
        console.error("获取设备失败:", error);
        this.currentPartitionDevices = [];
      }
    },

    handleDeviceSelectionChange(selectedIds) {
      this.selectedDeviceIds = selectedIds;
    },

    selectAllDevices() {
      const availableIds = this.currentPartitionDevices
        .map(d => d.id)
        .filter(id => !this.rightSelectedIds.includes(id));
      this.selectedDeviceIds = [
        ...new Set([...this.selectedDeviceIds, ...availableIds])
      ];
    },

    clearSelection() {
      this.selectedDeviceIds = [];
    },

    syncLeftSelection() {
      this.selectedDeviceIds = this.selectedDeviceIds.filter(
        id => !this.rightSelectedIds.includes(id)
      );
    },

    syncFormData() {
      if (!this.form || this.isLoadingEcho) return;

      this.form.equipmentIds = this.rightSelectedIds.length
        ? this.rightSelectedIds.join(",")
        : null;

      const regionIdSet = new Set(
        this.selectedItems.map(item => item.partitionId)
      );
      this.form.regionIds = regionIdSet.size
        ? Array.from(regionIdSet).join(",")
        : null;
    },

    moveToRight() {
      if (this.selectedDeviceIds.length === 0) return;

      this.selectedDeviceIds.forEach(deviceId => {
        const device = this.currentPartitionDevices.find(
          d => d.id === deviceId
        );
        if (device && !this.rightSelectedIds.includes(deviceId)) {
          this.rightSelectedIds.push(deviceId);
          this.selectedItems.push({
            deviceId,
            partitionId: this.currentPartition.id,
            partitionName: this.currentPartition.regionName,
            deviceName: device.name
          });
        }
      });

      this.selectedDeviceIds = [];
      this.syncLeftSelection();
    },

    moveToLeft() {
      if (this.rightSelectedIds.length === 0) return;
      this.rightSelectedIds = [];
      this.selectedItems = [];
      this.syncLeftSelection();
    },

    removeFromRight(deviceId) {
      const index = this.rightSelectedIds.indexOf(deviceId);
      if (index > -1) {
        this.rightSelectedIds.splice(index, 1);
        this.selectedItems = this.selectedItems.filter(
          item => item.deviceId !== deviceId
        );
        this.syncLeftSelection();
      }
    },

    submit() {
      this.syncFormData();
      if (!this.rightSelectedIds.length) {
        this.$message.warning("请至少选择一个设备");
        return;
      }
      this.$emit("success");
    },

    getSelectedDeviceIds() {
      return [...this.rightSelectedIds];
    },

    getSelectedItems() {
      return [...this.selectedItems];
    }
  },

  mounted() {
    this.getROLE();
    this.getFenqu();
  }
};
</script>

<style scoped lang="scss">
/* 样式保持不变，此处省略 */
.tree-transfer {
  display: flex;
  gap: 16px;
  padding: 16px;

  .transfer-panel {
    flex: 1;
    border: 1px solid var(--input-border);
    border-radius: 4px;
    overflow: hidden;

    .panel-header {
      padding: 12px 16px;
      background: var(--card-bg);
      font-weight: bold;
      border-bottom: 1px solid var(--table-border);
    }

    .partition-tree {
      height: 400px;
      padding: 8px;
    }

    .device-section {
      padding: 12px 16px;

      .section-title {
        margin-bottom: 12px;
        font-weight: 600;
        color: var(--text-main);
      }

      .device-item {
        display: block;
        margin-bottom: 8px;
        padding: 4px 8px;
        border-radius: 4px;
        cursor: pointer;

        &:hover {
          background: var(--card-bg);
        }
      }

      .device-actions {
        margin-top: 12px;
        display: flex;
        gap: 8px;
      }
    }

    .selected-list {
      padding: 12px;

      .selected-item {
        margin: 4px;
        cursor: pointer;
      }
    }
  }

  .transfer-buttons {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 8px;

    .el-button {
      width: 40px;
      height: 40px;
      padding: 0;
    }
  }
}

.device-scrollbar,
.selected-scrollbar {
  .el-scrollbar__view {
    overflow: auto;
  }
}
</style>
